/**
 * Generate cabinet column layout based on sink count and positions.
 *
 * - Under sinks: 'doors' (plumbing access, 24" wide)
 * - Everywhere else: 'drawers' (more functional storage)
 * - Cabinets 48" or less with a single sink: single doors column (no drawers)
 * - Drawer columns must be at least 12" wide; otherwise absorbed into doors
 *
 * Each column has a width (meters) and type.
 * Column widths sum to the full cabinet width — a door column beside a
 * basin that sits closer than 12" to the end is CLIPPED at the end rather
 * than allowed to run past it. That invariant is load-bearing, not
 * cosmetic: `cabinetRenderer` lays the columns out from `-dimensions.x / 2`
 * along local +x with no bound of its own, so an over-wide set draws the
 * fronts straight out of the box (haines Scan 1 bath B88124DF: a resize
 * left both basins 13.75" from the ends, the two 24" doors summed 3.76"
 * wider than the carcass, and the whole overhang landed on one end, 2.4"
 * into the linen tower beside it).
 *
 * @param {number} cabinetWidth - Total cabinet width in meters
 * @param {number} sinkCount - Number of sinks (0, 1, or 2)
 * @param {number[]} sinkLateralOffsets - Lateral offsets from cabinet center (meters, in cabinet local X)
 * @returns {{ width: number, type: string }[]}
 */
function generateCabinetColumns(cabinetWidth, sinkCount, sinkLateralOffsets) {
  const { VANITY_DIMENSIONS, INCHES_TO_METERS } = require('./constants');
  const sinkWidth = VANITY_DIMENSIONS.sink.width; // 24 inches
  const MIN_DRAWER_WIDTH = 12 * INCHES_TO_METERS; // 12 inches
  const MAX_SINGLE_SINK_CABINET = 48 * INCHES_TO_METERS; // 48 inches

  if (!sinkCount || sinkCount === 0 || !sinkLateralOffsets?.length) {
    return [{ width: cabinetWidth, type: 'doors' }];
  }

  // Single sink on a cabinet 48" or less: just one doors column
  if (sinkCount === 1 && cabinetWidth <= MAX_SINGLE_SINK_CABINET) {
    return [{ width: cabinetWidth, type: 'doors' }];
  }

  // Sort offsets left to right
  const sorted = [...sinkLateralOffsets].sort((a, b) => a - b);

  const columns = [];
  const rightEdge = cabinetWidth / 2;
  let cursor = -cabinetWidth / 2;

  for (const offset of sorted) {
    // The basin's 24" door, clipped to what is actually left of the box:
    // it can't start before the previous column ended, and it can't run
    // past the right edge. Without the clamps a basin nearer the end than
    // half a door width pushes the cursor outside the carcass and the
    // widths stop summing to `cabinetWidth`.
    const doorsLeft = Math.max(cursor, offset - sinkWidth / 2);
    const doorsRight = Math.min(rightEdge, offset + sinkWidth / 2);
    const doorsWidth = doorsRight - doorsLeft;
    if (doorsWidth <= 0.001) continue; // basin outside the cabinet

    const drawersWidth = doorsLeft - cursor;
    if (drawersWidth >= MIN_DRAWER_WIDTH) {
      columns.push({ width: drawersWidth, type: 'drawers' });
    } else if (drawersWidth > 0.001) {
      // Too narrow for drawers — absorb into the doors column
      columns.push({ width: drawersWidth, type: 'doors' });
    }

    columns.push({ width: doorsWidth, type: 'doors' });
    cursor = doorsRight;
  }

  // Remaining space on the right
  const remaining = rightEdge - cursor;
  if (remaining >= MIN_DRAWER_WIDTH) {
    columns.push({ width: remaining, type: 'drawers' });
  } else if (remaining > 0.001) {
    // Too narrow for drawers — absorb into doors
    columns.push({ width: remaining, type: 'doors' });
  }

  return columns;
}

module.exports = { generateCabinetColumns };
